import { createApi } from '@reduxjs/toolkit/query/react';
import baseQueryWithTokenManagement from 'pages/auth/apiSlice';
import moment from 'moment/moment';
import { usersApi } from 'api/user';

export const rolePermissionApi = createApi({
  tagTypes: ['PERMISSION', 'ROLE_LIST'],
  reducerPath: 'rolePermissionApi',
  baseQuery: baseQueryWithTokenManagement,
  endpoints: (builder) => ({
    permissionList: builder.query<any, any>({
      query: (args) => {
        const { size = 10, page = 1, search = '' } = args;
        const number = page - 1;
        return {
          url: `/uaa/api/permission?size=${size}&page=${number}&search=${search}`,
        };
      },
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        return response?.data || {};
      },
    }),
    roleCreate: builder.mutation<any, any>({
      query: (body) => {
        return {
          url: `/uaa/api/role-management`,
          method: 'post',
          body,
        };
      },
      invalidatesTags: () => ['ROLE_LIST'],
    }),
    roleList: builder.query<any, any>({
      query: (args) => {
        const { size = 10, page = 1, sortField, sortOrder, search, filters } = args;
        const number = page - 1;
        let sortParam = '';
        if (sortField && sortOrder) {
          sortParam = `&sort=${sortField},${sortOrder}`;
        }
        const params = { ...filters, ...(typeof search !== 'undefined' && { search }) };
        return {
          url: `/uaa/api/role-management?size=${size}&page=${number}${sortParam}`,
          params: Object.keys(params).length ? params : undefined,
        };
      },
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        const content = response?.data?.content;
        if (Array.isArray(content)) {
          content[0].roleManagements = (content[0].roleManagements || []).map((item: any) => ({
            ...item,
            formattedLastModifiedDate: {
              full: moment(item.lastModifiedDate).format('DD.MM.YYYY HH:mm:ss'),
              day: moment(item.lastModifiedDate).format('DD.MM.YYYY'),
            },
            formattedCreatedDate: {
              full: moment(item.createDate).format('DD.MM.YYYY HH:mm:ss'),
              day: moment(item.createDate).format('DD.MM.YYYY'),
            },
            users: item.userIds?.map((id: string) => {
              return content[0].adminResponses.find((_admin: any) => _admin.id === id);
            }),
            key: item.id,
          }));
        }
        return response?.data;
      },
      providesTags: () => ['ROLE_LIST'],
    }),
    roleUpdate: builder.mutation<any, any>({
      query: (body) => {
        return {
          url: `/uaa/api/role-management`,
          method: 'put',
          body,
        };
      },
      invalidatesTags: () => ['ROLE_LIST'],
    }),
    roleDelete: builder.mutation<any, any>({
      query: (body) => {
        return {
          url: `/uaa/api/role-management`,
          method: 'delete',
          body,
        };
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(usersApi.util.invalidateTags(['USER_LIST']));
      },
      invalidatesTags: () => ['ROLE_LIST'],
    }),
    roleById: builder.query<any, any>({
      query: (args) => {
        const { id } = args;
        return {
          url: `/uaa/api/role-management/${id}`,
        };
      },
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        return {
          ...response.data,
          formattedLastModifiedDate: moment(response.data.lastModifiedDate).format('DD.MM.YYYY HH:mm:ss'),
          formattedCreatedDate: moment(response.data.createDate).format('DD.MM.YYYY HH:mm:ss'),
        };
      },
      providesTags: () => ['ROLE_LIST'],
    }),
    roleSelectList: builder.query<any, void>({
      query: () => {
        return {
          url: `/uaa/api/role-management/selector-list`,
        };
      },
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        response.data = (response?.data || []).map((item: any) => ({
          ...item,
          label: item.name,
          value: item.id,
        }));
        return response.data;
      },
      providesTags: () => ['ROLE_LIST'],
    }),
  }),
});

export const {
  usePermissionListQuery,
  useRoleCreateMutation,
  useRoleDeleteMutation,
  useRoleUpdateMutation,
  useRoleListQuery,
  useRoleByIdQuery,
  useRoleSelectListQuery,
} = rolePermissionApi;
